/**
 * Sysadmin System Library — shared lifecycle UI primitives (PR-J).
 *
 * Reusable across all six entity types (folders, articles, tags,
 * documents, document folders, sources). Renders:
 *   - StatusPill: badge with the lifecycle state
 *   - UnpublishDialog: required-reason + nextStatus selector
 *   - LifecycleActions: per-row action buttons
 *
 * Tenants never see these — they're sysadmin-only. The tenant System
 * Library view shows a simple "System" badge instead (lib-system-badge).
 */

import { useState } from 'react';
import {
  AlertCircle, CheckCircle2, MinusCircle, XCircle, Send,
  Undo2, Loader2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { LifecycleStatus } from '@/lib/use-sysadmin-library';

// ─── Status pill ───────────────────────────────────────────────────────────

const STATUS_TONE: Record<LifecycleStatus, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  draft: {
    label: 'Draft',
    className: 'bg-muted text-muted-foreground border-muted-foreground/20',
    Icon: MinusCircle,
  },
  published: {
    label: 'Published',
    className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    Icon: CheckCircle2,
  },
  deprecated: {
    label: 'Deprecated',
    className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
    Icon: AlertCircle,
  },
  retired: {
    label: 'Retired',
    className: 'bg-destructive/10 text-destructive border-destructive/30',
    Icon: XCircle,
  },
};

export function LibraryStatusPill({ status }: { status: LifecycleStatus }) {
  const tone = STATUS_TONE[status];
  const Icon = tone.Icon;
  return (
    <Badge variant="outline" className={`gap-1 text-[10px] uppercase tracking-wide ${tone.className}`}>
      <Icon className="h-3 w-3" />
      {tone.label}
    </Badge>
  );
}

// ─── Unpublish dialog ──────────────────────────────────────────────────────
// Reason is required; nextStatus picks deprecated (default — visible to
// existing assignments but not new tenants) vs retired (terminal — gone).

export interface UnpublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceLabel: string;
  busy?: boolean;
  onConfirm: (input: { reason: string; nextStatus: 'deprecated' | 'retired' }) => Promise<void>;
}

export function LibraryUnpublishDialog({
  open,
  onOpenChange,
  resourceLabel,
  busy = false,
  onConfirm,
}: UnpublishDialogProps) {
  const [reason, setReason] = useState('');
  const [nextStatus, setNextStatus] = useState<'deprecated' | 'retired'>('deprecated');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm({ reason: reason.trim(), nextStatus });
      onOpenChange(false);
      setReason('');
      setNextStatus('deprecated');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (busy || submitting) return;
        if (!o) {
          setReason('');
          setNextStatus('deprecated');
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Unpublish {resourceLabel}</DialogTitle>
          <DialogDescription>
            Tenants will lose visibility of this content immediately. The reason is recorded
            in the platform audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="next-status">Next status</Label>
            <Select
              value={nextStatus}
              onValueChange={(v) => setNextStatus(v as 'deprecated' | 'retired')}
            >
              <SelectTrigger id="next-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deprecated">
                  Deprecated — hidden from new tenants; existing assignments keep access
                </SelectItem>
                <SelectItem value="retired">
                  Retired — terminal; cannot be republished
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reason">
              Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Why is this being unpublished?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={2000}
            />
            <p className="text-[10px] text-muted-foreground">
              Required. Max 2000 characters.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy || submitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={busy || submitting || !reason.trim()}
          >
            {(busy || submitting) && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Unpublish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Lifecycle action buttons ──────────────────────────────────────────────
// Renders Publish or Unpublish (or both, depending on status). Caller
// handles the actual mutation through their own hook.

export interface LifecycleActionsProps {
  status: LifecycleStatus;
  onPublish: () => void;
  onUnpublishRequest: () => void;
  busy?: boolean;
  size?: 'sm' | 'default';
}

export function LibraryLifecycleActions({
  status,
  onPublish,
  onUnpublishRequest,
  busy = false,
  size = 'sm',
}: LifecycleActionsProps) {
  if (status === 'retired') {
    return (
      <span className="text-[10px] text-muted-foreground italic">Retired (terminal)</span>
    );
  }

  if (status === 'draft') {
    return (
      <Button
        size={size}
        onClick={onPublish}
        disabled={busy}
        className="gap-1.5"
        title="Publish — visible to all tenants"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        Publish
      </Button>
    );
  }

  if (status === 'published') {
    return (
      <Button
        size={size}
        variant="outline"
        onClick={onUnpublishRequest}
        disabled={busy}
        className="gap-1.5"
        title="Unpublish — hide from tenants"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
        Unpublish
      </Button>
    );
  }

  // deprecated → can re-publish
  return (
    <Button
      size={size}
      variant="outline"
      onClick={onPublish}
      disabled={busy}
      className="gap-1.5"
      title="Re-publish — restore tenant visibility"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
      Re-publish
    </Button>
  );
}
